import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiService from '../../../../services/apiService';

/**
 * One tournament, opened from "View Details" on the homepage.
 *
 * The link has always pointed here; the route never existed, so it landed on a
 * blank page. Everything shown comes from the event itself — no invented
 * content, and nothing at all when a field is empty.
 */
const ThrowballChampionshipDetail = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiService.get(`/public/events/${id}`);
        const body = res.data || {};
        if (!alive) return;

        if (body.event) {
          setEvent(body.event);
          setSimilar(Array.isArray(body.similar_events) ? body.similar_events : []);
        } else {
          setError('That tournament could not be found.');
        }
      } catch {
        if (alive) setError('That tournament could not be found.');
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => { alive = false; };
  }, [id]);

  const dateRange = (from, to) => {
    if (!from) return null;
    const opts = { day: 'numeric', month: 'short', year: 'numeric' };
    const start = new Date(from).toLocaleDateString('en-GB', opts);
    if (!to) return start;
    const end = new Date(to).toLocaleDateString('en-GB', opts);
    return start === end ? start : `${start} – ${end}`;
  };

  if (loading) {
    return (
      <section className="tb-page-section tb-news-detail-section">
        <div className="tb-container">
          <p style={{ color: '#6b7280' }}>Loading…</p>
        </div>
      </section>
    );
  }

  if (error || !event) {
    return (
      <section className="tb-page-section tb-news-detail-section">
        <div className="tb-container">
          <h1 style={{ fontSize: '1.6rem', marginBottom: 10 }}>Tournament not found</h1>
          <p style={{ color: '#6b7280', marginBottom: 18 }}>{error}</p>
          <Link to="/championships" className="tb-btn tb-btn-primary">
            All tournaments<span className="tb-btn-arrow"></span>
          </Link>
        </div>
      </section>
    );
  }

  const facts = [
    ['City', event.event_city],
    ['Venue', event.event_venue],
    ['Dates', event.formatted_dates || dateRange(event.event_start_date, event.event_end_date)],
    ['Sport', event.event_category],
    ['Status', event.status_label],
    ['Registration closes', dateRange(event.registration_end_date, null)],
    ['Organiser', event.event_organizer],
  ].filter(([, value]) => value);

  return (
    <section className="tb-page-section tb-news-detail-section">
      <div className="tb-container">
        {/* Not tb-page-banner: that reserves height for a background image
            these events do not have, leaving a tall blank box. */}
        <div style={{ marginBottom: 22 }}>
          <h1 style={{ fontSize: '2rem', margin: '0 0 6px', color: '#111827' }}>
            {event.event_name}
          </h1>
          <span style={{
            display: 'inline-block', height: 3, width: 54, borderRadius: 2,
            background: 'linear-gradient(90deg,#e91e63,#ff9800)',
          }} />
        </div>

        <div className="tb-page-content">
          {event.event_description && (
            <p style={{ fontSize: '1.02rem', lineHeight: 1.65, color: '#374151', marginBottom: 26 }}>
              {event.event_description}
            </p>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: 14,
            marginBottom: 30,
          }}>
            {facts.map(([label, value]) => (
              <div key={label} style={{
                background: '#f8fafc',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                padding: '14px 16px',
              }}>
                <div style={{
                  fontSize: '0.72rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#6b7280',
                  fontWeight: 700,
                  marginBottom: 5,
                }}>{label}</div>
                <div style={{ fontSize: '0.98rem', fontWeight: 600, color: '#111827' }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          {event.eligibility_criteria && (
            <>
              <h2 style={{ fontSize: '1.15rem', marginBottom: 8 }}>Who can enter</h2>
              <p style={{ color: '#374151', lineHeight: 1.65, marginBottom: 26 }}>
                {event.eligibility_criteria}
              </p>
            </>
          )}

          {similar.length > 0 && (
            <>
              <h2 style={{ fontSize: '1.15rem', marginBottom: 12 }}>Also running</h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 12,
                marginBottom: 26,
              }}>
                {similar.slice(0, 6).map((other) => (
                  <Link key={other.id} to={`/championships/${other.id}`} style={{
                    display: 'block',
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 10,
                    padding: '13px 15px',
                    textDecoration: 'none',
                    color: '#111827',
                  }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{other.event_name}</div>
                    <div style={{ color: '#6b7280', fontSize: '0.83rem', marginTop: 4 }}>
                      {[other.event_city, other.formatted_dates].filter(Boolean).join(' · ')}
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}

          <Link to="/championships" className="tb-btn tb-btn-primary">
            All tournaments<span className="tb-btn-arrow"></span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ThrowballChampionshipDetail;
